function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Card, Pill, Empty, Ribbon } from "../../components/analytics-kit";
import GeoMap from "../../components/geo-map";
import Auth from "../../functions/authenticate";
import { hasTenantFeature } from "../../functions/tenant-features";
import { num } from "../sales/territory-common";
import { uploadTerritories, applyTerritoryUpload, rollbackTerritoryUpload, listTerritoryUploads, getGeoCoverage, isSoft } from "../sales/sales-api";
import DataShell, { Btn, Notice, isAdmin } from "./shell";

/*
 * Manage Data, Territories (SALES_TERRITORY_MAPS.md §5.1; layout per the 2026-10 Territories mockup).
 * Ribbon (only the tiles the coverage read and the upload list can back), then two columns:
 * "Change who covers where" as three steps (what's in the file = upload kind, what to do = mode,
 * upload = drop zone with the paste box behind "or paste rows"), the review and save flow, and on
 * the right the coverage map, unchanged. Upload history below in plain lines with Undo.
 *
 * Three CSV shapes, one grid:
 *   zip, rep_code[, channel][, priority][, territory]   (zip_rep)
 *   zip, territory[, channel]                            (zip_territory)
 *   state, rep_code[, channel]                           (state_rep, the legacy shape)
 * Every upload runs as a DRY RUN first: POST /v1/sales/territories/upload returns
 * the validation report (unknown rep = block; overlap, terminated rep, non-US ZIP =
 * warn; gap, rep with no ZIPs, replaces state grain = info). Apply writes the rows
 * under one upload id (POST .../upload/{id}/apply); Rollback undoes an upload as a
 * unit (POST .../upload/{id}/rollback). Mode merge (default) or replace.
 * The file is parsed client-side too, so the shape and row count show before the
 * server answers, and the page says so honestly when the rail is not deployed.
 */
const KINDS = [["zip_rep", "ZIP to rep"], ["zip_territory", "ZIP to territory"], ["state_rep", "State to rep (legacy)"]];
// Plain words for the three upload kinds and the two modes (step 1 and step 2 of the card).
const KIND_WORDS = {
  zip_rep: "A rep for each ZIP",
  zip_territory: "A territory for each ZIP",
  state_rep: "A rep for each state"
};
const KIND_HINT = {
  zip_rep: "Columns: ZIP, rep code, channel, priority, territory name. One row per ZIP for each rep.",
  zip_territory: "Columns: ZIP, territory name, channel. One row per ZIP.",
  state_rep: "Columns: state, rep code, channel. One row per state for each rep."
};
const MODES = [["merge", "Add and update these ZIPs"], ["replace", "Replace everything"]];
const MODE_WORDS = {
  merge: "add and update",
  replace: "replace everything"
};
const KIND_COLS = {
  zip_rep: ["zip", "rep_code", "channel", "priority", "territory"],
  zip_territory: ["zip", "territory", "channel"],
  state_rep: ["state", "rep_code", "channel"]
};
const TEMPLATE = {
  zip_rep: "zip,rep_code,channel,priority,territory\n33101,WHIT-FL,residential,1,South Florida\n33101,MARC-TX,residential,2,South Florida\n30301,HALL-GA,residential,,Atlanta metro\n",
  zip_territory: "zip,territory,channel\n33101,South Florida,residential\n30301,Atlanta metro,\n",
  state_rep: "state,rep_code,channel\nFL,WHIT-FL,residential\nGA,HALL-GA,\n"
};
const SEV_TONE = {
  block: "danger",
  warn: "warning",
  warning: "warning",
  info: "neutral",
  notice: "info"
};
const asDate = d => {
  if (!d) return null;
  const x = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(String(d)) ? d : `${d}Z`);
  return isNaN(x.getTime()) ? null : x;
};
const fmtWhen = d => {
  const x = asDate(d);
  return x ? x.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }) : "·";
};
function parseCsv(text) {
  const lines = String(text || "").split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (!lines.length) return {
    header: [],
    rows: 0
  };
  const header = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
  const hasHeader = header.some(h => /zip|rep|state|territory|channel/.test(h));
  return {
    header: hasHeader ? header : [],
    rows: hasHeader ? lines.length - 1 : lines.length
  };
}
function guessKind(header) {
  const h = (header || []).join(",");
  if (/state/.test(h) && !/zip/.test(h)) return "state_rep";
  if (/zip/.test(h) && /territory/.test(h) && !/rep/.test(h)) return "zip_territory";
  return "zip_rep";
}
class TerritoriesView extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      kind: "zip_rep",
      mode: "merge",
      file: null,
      text: "",
      parsed: null,
      report: undefined,
      busy: false,
      notice: null,
      noticeTone: "info",
      uploads: undefined,
      coverage: undefined,
      mappings: {},
      showAll: {},
      confirmRollback: null,
      railMissing: false,
      pasteOpen: false
    });
    _defineProperty(this, "pick", e => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result || "");
        const parsed = parseCsv(text);
        this.setState({
          file,
          text,
          parsed,
          kind: parsed.header.length ? guessKind(parsed.header) : this.state.kind,
          report: undefined,
          mappings: {}
        }, this.dryRun);
      };
      reader.readAsText(file);
    });
    _defineProperty(this, "onPaste", text => {
      const parsed = parseCsv(text);
      this.setState({
        file: null,
        text,
        parsed: parsed.rows ? parsed : null,
        kind: parsed.header.length ? guessKind(parsed.header) : this.state.kind,
        report: undefined,
        mappings: {}
      });
    });
    _defineProperty(this, "dryRun", async () => {
      const {
        file,
        text,
        kind,
        mode
      } = this.state;
      if (!file && !text.trim()) return;
      this.setState({
        busy: true,
        notice: null,
        report: undefined
      });
      try {
        const report = await uploadTerritories(file, {
          kind,
          mode,
          text: file ? undefined : text
        });
        if (!this._alive) return;
        this.setState({
          busy: false,
          report,
          railMissing: report === null
        });
      } catch (e) {
        if (!this._alive) return;
        this.setState({
          busy: false,
          report: null,
          notice: e && e.status === 403 ? "Only owners and administrators can change territories." : e && e.data && e.data.error || "The file couldn't be checked. Make sure the columns match the template and try again.",
          noticeTone: "danger"
        });
      }
    });
    _defineProperty(this, "apply", async () => {
      const {
        report,
        mappings
      } = this.state;
      if (!report || !report.id) return;
      this.setState({
        busy: true,
        notice: null
      });
      try {
        const r = await applyTerritoryUpload(report.id, Object.keys(mappings).length ? mappings : undefined);
        if (!this._alive) return;
        this.setState({
          busy: false,
          report: {
            ...report,
            status: "applied"
          },
          notice: `Saved ${num(r && (r.applied != null ? r.applied : r.rows) || report.summary.applicable || report.rows)} rows. The map, the Coverage tab and the rep pages show the new territories from now on.`,
          noticeTone: "info",
          file: null,
          text: "",
          parsed: null
        });
        this.load();
      } catch (e) {
        if (!this._alive) return;
        this.setState({
          busy: false,
          notice: e && e.data && e.data.error || (e && e.status === 409 ? "A row still has a problem. Fix the file or match the rep, then check it again." : "That didn't save. Check the file again and retry."),
          noticeTone: "danger"
        });
      }
    });
    _defineProperty(this, "rollback", async id => {
      this.setState({
        busy: true,
        notice: null,
        confirmRollback: null
      });
      try {
        await rollbackTerritoryUpload(id);
        if (!this._alive) return;
        this.setState({
          busy: false,
          notice: "Upload undone. Its rows are removed and the territories from before it are back.",
          noticeTone: "info"
        });
        this.load();
      } catch (e) {
        if (!this._alive) return;
        this.setState({
          busy: false,
          notice: e && e.data && e.data.error || "That upload couldn't be undone. Try again.",
          noticeTone: "danger"
        });
      }
    });
    _defineProperty(this, "mapRep", (bad, good) => this.setState(s => ({
      mappings: {
        ...s.mappings,
        [bad]: good
      }
    })));
    _defineProperty(this, "downloadTemplate", () => {
      const blob = new Blob([TEMPLATE[this.state.kind]], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `territories-${this.state.kind}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
    _defineProperty(this, "downloadList", issue => {
      if (issue.download) {
        window.open(issue.download, "_blank");
        return;
      }
      const rows = issue.rows || issue.zips || issue.items || issue.raw && (issue.raw.zips || issue.raw.rows) || [];
      if (!rows.length) return;
      const blob = new Blob([rows.map(r => typeof r === "object" ? Object.values(r).join(",") : String(r)).join("\n") + "\n"], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${issue.check || "list"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
  }
  async componentDidMount() {
    this._alive = true;
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load() {
    const [uploads, coverage] = await Promise.all([listTerritoryUploads().catch(() => null), getGeoCoverage({}).catch(() => null)]);
    if (!this._alive) return;
    this.setState({
      uploads,
      coverage,
      railMissing: uploads === null
    });
  }
  renderIssue(i, idx) {
    const {
      mappings,
      showAll
    } = this.state;
    const sev = i.severity === "warning" ? "warn" : i.severity;
    const canMap = sev === "block" && i.repCode && i.suggestion;
    const mapped = canMap && mappings[i.repCode] === i.suggestion;
    return /*#__PURE__*/React.createElement("div", {
      key: idx,
      className: `sx-issue${sev === "block" && !mapped ? " block" : ""}`
    }, /*#__PURE__*/React.createElement(Pill, {
      tone: SEV_TONE[sev] || "neutral"
    }, sev), /*#__PURE__*/React.createElement("div", {
      className: "m"
    }, i.row != null && !/^Row \d/.test(i.message || "") ? `Row ${num(i.row)}: ` : "", i.message, i.detail ? /*#__PURE__*/React.createElement("small", null, i.detail) : i.suggestion && sev === "block" ? /*#__PURE__*/React.createElement("small", null, "Did you mean ", /*#__PURE__*/React.createElement("code", null, i.suggestion), "? Fix the file or match it here.") : null), /*#__PURE__*/React.createElement("span", null, canMap ? mapped ? /*#__PURE__*/React.createElement(Pill, {
      tone: "success"
    }, "matched to ", i.suggestion) : /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.mapRep(i.repCode, i.suggestion)
    }, "Match to ", i.suggestion) : null, i.count > 1 && (i.rows || i.zips || i.items) ? /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.setState({
        showAll: {
          ...showAll,
          [idx]: !showAll[idx]
        }
      })
    }, showAll[idx] ? "Hide" : `Show ${num(i.count)}`) : null, (i.download || i.zips || i.rows) && !(i.count > 1 && (i.rows || i.zips || i.items)) ? /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.downloadList(i)
    }, "Download") : null), showAll[idx] ? /*#__PURE__*/React.createElement("div", {
      className: "sx-issue-list"
    }, (i.rows || i.zips || i.items || []).slice(0, 200).map((r, k) => /*#__PURE__*/React.createElement("span", {
      key: k
    }, typeof r === "object" ? Object.values(r).join(" · ") : String(r)))) : null);
  }
  ribbonTiles() {
    const {
      coverage,
      uploads
    } = this.state;
    const sum = coverage && coverage.summary || {};
    const pick = (...keys) => {
      for (const k of keys) {
        const v = sum[k];
        if (v != null && v !== "" && !isNaN(Number(v))) return Number(v);
      }
      return null;
    };
    const tiles = [];
    const covered = pick("coveredZips", "CoveredZips");
    const repCount = coverage && coverage.legend ? coverage.legend.length : 0;
    if (covered != null) tiles.push({
      l: "ZIP codes covered",
      v: num(covered),
      s: repCount ? `covered by the ${num(repCount)} rep${repCount === 1 ? "" : "s"} with an area assigned` : ""
    });
    const dealers = pick("uncoveredDealers", "dealersWithoutRep", "dealersUncovered");
    const uncZips = pick("uncoveredZips", "UncoveredZips");
    if (dealers != null) tiles.push({
      l: "Dealers with no rep",
      v: num(dealers),
      s: "in ZIPs nobody covers"
    });else if (uncZips != null) tiles.push({
      l: "ZIPs with dealers and no rep",
      v: num(uncZips),
      s: "nobody covers them yet"
    });
    const shared = pick("overlaps", "overlapZips", "sharedZips");
    if (shared != null) tiles.push({
      l: "Shared ZIPs",
      v: num(shared),
      s: "covered by more than one rep"
    });
    const last = (uploads || []).find(u => u.status === "applied" || u.status === "rolled_back");
    if (last) {
      const d = asDate(last.appliedDate || last.createdDate);
      tiles.push({
        l: "Last change",
        v: d ? d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric"
        }) : "·",
        s: [last.status === "rolled_back" ? "undone" : null, last.createdBy ? `by ${last.createdBy}` : null].filter(Boolean).join(", ")
      });
    }
    return tiles;
  }
  uploadLine(u, admin) {
    const {
      confirmRollback,
      busy
    } = this.state;
    const what = `${u.fileName || "pasted rows"}, ${num(u.rows)} row${u.rows === 1 ? "" : "s"}`;
    const how = `${(KIND_WORDS[u.kind] || u.kind || "").toLowerCase()}, ${MODE_WORDS[u.mode] || u.mode}`;
    const verb = u.status === "applied" ? `saved ${num(u.applied || u.rows)} rows from` : u.status === "rolled_back" ? "undid" : "checked, but didn't save,";
    return /*#__PURE__*/React.createElement("div", {
      className: "dd-line static",
      key: u.id
    }, /*#__PURE__*/React.createElement("span", {
      className: `dot ${u.status === "applied" ? "success" : "neutral"}`,
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("span", {
      className: "w"
    }, u.createdBy || "Someone", " ", verb, " ", what, " ", /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "(", how, ")")), /*#__PURE__*/React.createElement("span", {
      className: "d"
    }, fmtWhen(u.appliedDate || u.createdDate)), u.status === "applied" && admin ? confirmRollback === u.id ? /*#__PURE__*/React.createElement("span", {
      className: "acts"
    }, /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink danger",
      onClick: () => !busy && this.rollback(u.id)
    }, "Yes, undo it"), /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink muted",
      onClick: () => this.setState({
        confirmRollback: null
      })
    }, "Keep")) : /*#__PURE__*/React.createElement("span", {
      className: "acts"
    }, /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink muted",
      onClick: () => this.setState({
        confirmRollback: u.id
      })
    }, "Undo")) : null);
  }
  render() {
    const {
      kind,
      mode,
      file,
      text,
      parsed,
      report,
      busy,
      notice,
      noticeTone,
      uploads,
      coverage,
      railMissing,
      pasteOpen
    } = this.state;
    const admin = isAdmin();
    const applicable = report ? typeof report.summary.applicable === "number" ? report.summary.applicable : Math.max(0, report.rows - report.blocking) : 0;
    const tiles = this.ribbonTiles();
    return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Territories",
      subtitle: "Which rep covers which ZIP codes",
      actions: /*#__PURE__*/React.createElement(Btn, {
        onClick: this.downloadTemplate
      }, "Download a template")
    }, !hasTenantFeature("territoryRep") ? /*#__PURE__*/React.createElement(Notice, {
      tone: "warning"
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, "Territories need your sales rep file first, so there are reps to check uploads against. Add it on the Connect tab.")) : null, !admin ? /*#__PURE__*/React.createElement(Notice, {
      tone: "warning"
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, "Only owners and administrators can change territories. You can still see the current coverage.")) : null, railMissing ? /*#__PURE__*/React.createElement(Notice, {
      tone: "warning"
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, "Territory uploads aren't available here yet. Your file can be read, but checking, saving and undoing are not switched on.")) : null, notice ? /*#__PURE__*/React.createElement(Notice, {
      tone: noticeTone,
      onClose: () => this.setState({
        notice: null
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, notice)) : null, tiles.length ? /*#__PURE__*/React.createElement(Ribbon, {
      tiles: tiles,
      comparing: null,
      cols: tiles.length >= 4 ? 4 : tiles.length === 3 ? 3 : undefined
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: "wa-grid dd-terr"
    }, /*#__PURE__*/React.createElement(Card, {
      span: 6,
      title: "Change who covers where",
      cls: "dd-change"
    }, /*#__PURE__*/React.createElement("div", {
      className: "dd-step"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dd-step-l"
    }, "1 \xB7 What's in your file"), /*#__PURE__*/React.createElement("div", {
      className: "dd-seg three",
      role: "radiogroup",
      "aria-label": "What's in your file"
    }, KINDS.map(([k]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      role: "radio",
      "aria-checked": kind === k,
      className: kind === k ? "on" : "",
      disabled: !admin,
      onClick: () => this.setState({
        kind: k,
        report: undefined
      })
    }, KIND_WORDS[k])))), /*#__PURE__*/React.createElement("div", {
      className: "dd-step"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dd-step-l"
    }, "2 \xB7 What to do with it"), /*#__PURE__*/React.createElement("div", {
      className: "dd-seg two",
      role: "radiogroup",
      "aria-label": "What to do with it"
    }, MODES.map(([m, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: m,
      role: "radio",
      "aria-checked": mode === m,
      className: mode === m ? "on" : "",
      disabled: !admin,
      onClick: () => this.setState({
        mode: m,
        report: undefined
      })
    }, l))), /*#__PURE__*/React.createElement("span", {
      className: "dd-hint"
    }, mode === "merge" ? "Adds the ZIPs in your file and updates any that changed. Everything else stays." : "Removes every ZIP the reps in your file had before, then saves only what's in the file.")), /*#__PURE__*/React.createElement("div", {
      className: "dd-step"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dd-step-l"
    }, "3 \xB7 Upload it"), /*#__PURE__*/React.createElement("label", {
      className: `sx-drop dd-drop${admin ? "" : " off"}`
    }, /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: ".csv,text/csv",
      style: {
        display: "none"
      },
      disabled: !admin,
      onChange: this.pick
    }), /*#__PURE__*/React.createElement("b", null, file ? file.name : "Choose a spreadsheet"), /*#__PURE__*/React.createElement("span", null, parsed ? `${num(parsed.rows)} rows${parsed.header.length ? `, columns ${parsed.header.join(", ")}` : ", no header row found"}` : KIND_HINT[kind]), /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "CSV file")), pasteOpen || !file && text ? /*#__PURE__*/React.createElement("textarea", {
      className: "dd-paste",
      rows: 4,
      placeholder: TEMPLATE[kind],
      value: file ? "" : text,
      disabled: !admin,
      onChange: e => this.onPaste(e.target.value)
    }) : /*#__PURE__*/React.createElement("a", {
      className: "dd-link",
      onClick: () => admin && this.setState({
        pasteOpen: true
      })
    }, "or paste rows"), !file && text.trim() ? /*#__PURE__*/React.createElement("div", {
      className: "dd-actions"
    }, /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      disabled: !admin || busy,
      onClick: this.dryRun
    }, busy && report === undefined ? "Checking…" : "Check these rows")) : null, file && report === undefined && busy ? /*#__PURE__*/React.createElement("span", {
      className: "dd-hint"
    }, "Checking your file\u2026") : null, file && report === undefined && !busy ? /*#__PURE__*/React.createElement("div", {
      className: "dd-actions"
    }, /*#__PURE__*/React.createElement(Btn, {
      disabled: !admin,
      onClick: this.dryRun
    }, "Check it again")) : null), /*#__PURE__*/React.createElement("span", {
      className: "dd-hint"
    }, "You'll see what changes on the map before anything is saved, and any upload can be undone."), report ? /*#__PURE__*/React.createElement("div", {
      className: "dd-review"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dd-step-l"
    }, "Review"), /*#__PURE__*/React.createElement("div", {
      className: "sx-report"
    }, report.issues.length ? report.issues.map((i, idx) => this.renderIssue(i, idx)) : /*#__PURE__*/React.createElement("div", {
      className: "sx-issue"
    }, /*#__PURE__*/React.createElement(Pill, {
      tone: "success"
    }, "ok"), /*#__PURE__*/React.createElement("div", {
      className: "m"
    }, "Everything checks out. Every rep is known, every ZIP is a US ZIP, and no ZIP is shared."), /*#__PURE__*/React.createElement("span", null))), /*#__PURE__*/React.createElement("div", {
      className: "dd-actions"
    }, report.status === "applied" ? /*#__PURE__*/React.createElement(Pill, {
      tone: "success"
    }, "saved") : /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      disabled: !admin || busy || report.blocking > 0 && Object.keys(this.state.mappings).length < report.blocking || !report.id,
      onClick: this.apply
    }, busy ? "Saving…" : `Save ${num(applicable)} rows`), report.blocking > 0 && report.status !== "applied" ? /*#__PURE__*/React.createElement("span", {
      className: "dd-hint"
    }, "You can save once every problem row is fixed or matched to a rep.") : null, report.summary.reps || report.summary.channels.length ? /*#__PURE__*/React.createElement("span", {
      className: "dd-hint"
    }, report.summary.reps ? `${num(report.summary.reps)} reps` : "", report.summary.channels.length ? `${report.summary.reps ? ", " : ""}channel ${report.summary.channels.join(", ")}` : "") : null)) : report === null && (file || text.trim()) ? /*#__PURE__*/React.createElement(Empty, null, "The file couldn't be checked: territory uploads aren't available here yet.") : null), /*#__PURE__*/React.createElement(Card, {
      span: 6,
      title: "Coverage map",
      hint: "coverage at ZIP grain \xB7 hatched = ZIPs with dealers and no rep \xB7 outlined = overlap"
    }, coverage === undefined ? /*#__PURE__*/React.createElement(Empty, null, "Loading coverage") : coverage && coverage.hexes.length ? /*#__PURE__*/React.createElement(GeoMap, {
      height: 360,
      coverage: coverage,
      dealers: null,
      stores: null,
      heat: null,
      compact: true,
      defaultLayers: {
        coverage: true
      }
    }) : /*#__PURE__*/React.createElement(Empty, null, coverage === null ? "The ZIP coverage read is not deployed here yet." : "No ZIP assignments yet. The map lights up after the first applied upload; the same read powers the executive Coverage tab."), /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "The map shows who covers where today and reloads after you save an upload. ", /*#__PURE__*/React.createElement(Link, {
      to: "/Sales/this_year/coverage"
    }, "Open the Coverage tab"), ".")), /*#__PURE__*/React.createElement(Card, {
      span: 12,
      title: "Uploads",
      cls: "dd-list"
    }, uploads === undefined ? /*#__PURE__*/React.createElement(Empty, null, "Loading uploads") : !uploads || !uploads.length ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, uploads === null ? "Upload history isn't available here yet." : "No uploads yet. Your first one will show up here.") : /*#__PURE__*/React.createElement("div", {
      className: "dd-lines"
    }, uploads.map(u => this.uploadLine(u, admin))))));
  }
}
export default withRouter(TerritoriesView);